import React, { useEffect, useState, useRef } from 'react';
import { PhoneIcon, ShieldCheckIcon, StarIcon, BuildingIcon } from 'lucide-react';
function useCountUp(target: number, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStarted(true);
    }, {
      threshold: 0.5
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return {
    count,
    ref
  };
}
export function CTABannerSection() {
  const jobs = useCountUp(300);
  const reviews = useCountUp(150);
  const cities = useCountUp(12);
  return <section id="contact" className="w-full py-20 px-4 relative overflow-hidden" style={{
    backgroundColor: '#0283C7'
  }} data-id="element-82">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
      opacity: 0.06
    }} data-id="element-83">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-84">
          <defs data-id="element-85">
            <pattern id="grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-86">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="1" data-id="element-87" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" data-id="element-88" />
        </svg>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-89">
        <h2 className="font-bold text-white mb-4" style={{
        fontFamily: 'Montserrat, sans-serif',
        fontSize: 'clamp(28px, 5vw, 48px)',
        fontWeight: 900,
        lineHeight: 1.1
      }} data-id="element-90">
          Ready for a Cleaner Property?
        </h2>
        <p className="mb-10" style={{
        color: 'rgba(255,255,255,0.85)',
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        lineHeight: 1.6
      }} data-id="element-91">
          Get your free, no-obligation quote today. Licensed, bonded, and
          insured.
        </p>

        {/* Stat Counters */}
        <div className="grid grid-cols-3 gap-6 mb-12 max-w-2xl mx-auto" data-id="element-92">
          <div ref={jobs.ref} className="text-center" data-id="element-93">
            <div className="font-bold text-white" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 900,
            lineHeight: 1
          }} data-id="element-94">
              {jobs.count}+
            </div>
            <p className="text-white text-sm mt-1" style={{
            fontFamily: 'Inter, sans-serif',
            opacity: 0.85
          }} data-id="element-95">
              Jobs Completed
            </p>
          </div>
          <div ref={reviews.ref} className="text-center" data-id="element-96">
            <div className="font-bold text-white" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 900,
            lineHeight: 1
          }} data-id="element-97">
              {reviews.count}+
            </div>
            <p className="text-white text-sm mt-1" style={{
            fontFamily: 'Inter, sans-serif',
            opacity: 0.85
          }} data-id="element-98">
              Five-Star Reviews
            </p>
          </div>
          <div ref={cities.ref} className="text-center" data-id="element-99">
            <div className="font-bold text-white" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 900,
            lineHeight: 1
          }} data-id="element-100">
              {cities.count}+
            </div>
            <p className="text-white text-sm mt-1" style={{
            fontFamily: 'Inter, sans-serif',
            opacity: 0.85
          }} data-id="element-101">
              Cities Served
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-102">
          <a href="#quote" className="font-bold text-center py-4 px-10 rounded-lg transition-all hover:scale-[1.03] min-h-[54px] flex items-center justify-center" style={{
          backgroundColor: '#ffffff',
          color: '#0283C7',
          fontFamily: 'Inter, sans-serif',
          fontSize: '17px',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
        }} data-id="element-103">
            Get a Free Quote
          </a>
          <a href="tel:4805998442" className="font-bold text-white text-center py-4 px-10 rounded-lg border-2 transition-all hover:bg-white hover:text-blue-600 min-h-[54px] flex items-center justify-center gap-2" style={{
          borderColor: '#ffffff',
          fontFamily: 'Inter, sans-serif',
          fontSize: '17px',
          borderRadius: '8px'
        }} data-id="element-104">
            <PhoneIcon className="h-5 w-5" data-id="element-105" />
            Call Now: (480) 599-8442
          </a>
        </div>

        {/* Trust Icons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 flex-wrap" data-id="element-106">
          <div className="flex items-center gap-2" data-id="element-107">
            <ShieldCheckIcon className="h-5 w-5 text-white opacity-90" data-id="element-108" />
            <span className="text-sm font-semibold text-white" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-109">
              Licensed &amp; Bonded &amp; Insured
            </span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-white opacity-30" data-id="element-110" />
          <div className="flex items-center gap-2" data-id="element-111">
            <StarIcon className="h-5 w-5 fill-current" style={{
            color: '#F59E0B'
          }} data-id="element-112" />
            <span className="text-sm font-semibold text-white" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-113">
              150+ 5-Star Reviews
            </span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-white opacity-30" data-id="element-114" />
          <div className="flex items-center gap-2" data-id="element-115">
            <BuildingIcon className="h-5 w-5 text-white opacity-90" data-id="element-116" />
            <span className="text-sm font-semibold text-white" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-117">
              Trusted by Local Businesses
            </span>
          </div>
        </div>
      </div>
    </section>;
}